import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { supabase } from '../services/supabase';
import {
    Check, X, ChevronDown, ChevronUp, Info,
    Brain, Layers, PieChart, BarChart2, TrendingUp, Zap, Activity, Send
} from 'lucide-react';

// ─── Plan Definitions (order: Signals → Pro → Premium → Elite) ────────────────
const PLANS = [
    {
        name: 'Signals',
        tagline: 'Signals only — pure trading edge',
        price: '$30',
        amount: 30,
        period: '/month',
        popular: false,
        color: '#229ED9',
        badge: 'Signals Only',
        planKey: 'Signals',
        description: 'XAU/USD AI signals on dashboard + Telegram + auto-trading. No analysis suite. Pure signals, pure execution.',
        features: [
            'XAU/USD Neural AI Signals (ICT + Elite 53 Strategies)',
            'Exact Entry, Structural Stop Loss \u0026 Take Profit',
            'Telegram Real-Time Signal Alerts (auto-post)',
            'Signal History \u0026 Performance Log',
            'Autotrade: MT5 Auto-Execution Ready',
            'AI Confidence % on Every Signal',
            'Signal Dashboard Access (ICT Army + Elite 53)',
            'No Analysis Suite',
        ],
    },
    {
        name: 'Pro',
        tagline: 'Think like a hedge fund',
        price: '$30',
        amount: 30,
        period: '/month',
        popular: false,
        color: '#6366f1',
        badge: null,
        planKey: 'Pro',
        description: 'Full institutional AI analysis suite. 70 deep macro checks per month. No signals \u2014 pure research intelligence.',
        features: [
            '70 AI Fundamental Analyses/Month',
            'Fundamental Bias Dashboard (All Pairs)',
            'COT Report Visual Dashboard',
            'Institutional Order Flow (Order Blocks + FVGs)',
            'AI Chart \u0026 Market Structure Analysis',
            'Central Bank Divergence Tracker',
            'No Signals \u2014 Analysis Only',
        ],
    },
    {
        name: 'Premium',
        tagline: 'Trade Gold like a pro',
        price: '$50',
        amount: 50,
        period: '/month',
        popular: true,
        color: '#10B981',
        badge: 'Most Popular',
        planKey: 'Premium',
        description: '70 AI analyses per month + verified Gold (XAU/USD) signals with exact Entry, SL \u0026 TP.',
        features: [
            '70 AI Fundamental Analyses/Month',
            '3 AI-Verified Gold (XAU/USD) Signals/Day',
            '3-Layer Validation: Macro + Order Flow + COT',
            'Exact Entry, Structural Stop Loss \u0026 Take Profit',
            'Telegram Real-Time Signal Alerts',
            'Autotrade: MT5 Auto-Execution Ready',
            'Signal History \u0026 Performance Log',
            'AI Confidence % on Every Signal',
            'Full Analysis Suite (Fundamental, COT, Order Flow, Chart)',
        ],
    },
    {
        name: 'Elite',
        tagline: 'Maximum edge. No limits.',
        price: '$100',
        amount: 100,
        period: '/month',
        popular: false,
        color: '#D4A537',
        badge: 'Institutional Grade',
        planKey: 'Elite',
        description: 'Everything in Premium plus 250 deep AI analyses, 90%+ conviction elite-only signal setups, and priority execution.',
        features: [
            'Everything in Premium',
            '250 AI Fundamental Analyses/Month',
            '90%+ Confidence Gate \u2014 Elite-Only Setups',
            'Priority Signal Delivery (before Premium)',
            'Autotrade with Dynamic Sizing',
            'Quarterly Market Structure Alignment Gate',
            'Monthly AI Performance Report',
            'Early Access to New AI Features',
        ],
    },
];

// ─── Comparison Table Data ─────────────────────────────────────────────────────
const COMPARE_ROWS = [
    { category: 'Analysis', feature: 'AI Fundamental Analyses / Month', signals: 'None', pro: '70', premium: '70', elite: '250' },
    { category: 'Analysis', feature: 'Central Bank Divergence Tracker', signals: false, pro: true, premium: true, elite: true },
    { category: 'Analysis', feature: 'COT Report Visual Dashboard', signals: false, pro: true, premium: true, elite: true },
    { category: 'Analysis', feature: 'Institutional Order Flow (Order Blocks + FVGs)', signals: false, pro: true, premium: true, elite: true },
    { category: 'Analysis', feature: 'AI Chart & Market Structure Analysis', signals: false, pro: true, premium: true, elite: true },
    { category: 'Signals', feature: 'Gold (XAU/USD) Signals', signals: '✓ Unlimited', pro: false, premium: '3/day', elite: '3/day+' },
    { category: 'Signals', feature: 'ICT Neural AI Engine Signals', signals: true, pro: false, premium: true, elite: true },
    { category: 'Signals', feature: 'Elite 53 Strategy Signals', signals: true, pro: false, premium: true, elite: true },
    { category: 'Signals', feature: '3-Layer Validation (Macro + Order Flow + COT)', signals: false, pro: false, premium: true, elite: true },
    { category: 'Signals', feature: '90%+ Confidence Elite Setups', signals: false, pro: false, premium: false, elite: true },
    { category: 'Signals', feature: 'Exact Entry, Structural SL & TP', signals: true, pro: false, premium: true, elite: true },
    { category: 'Signals', feature: 'AI Confidence % on Every Signal', signals: true, pro: false, premium: true, elite: true },
    { category: 'Delivery', feature: 'Telegram Real-Time Alerts', signals: true, pro: false, premium: true, elite: true },
    { category: 'Delivery', feature: 'Priority Signal Delivery', signals: false, pro: false, premium: false, elite: true },
    { category: 'Execution', feature: 'Autotrade MT5 Integration', signals: true, pro: false, premium: true, elite: true },
    { category: 'Execution', feature: 'Dynamic Lot Sizing (by conviction %)', signals: false, pro: false, premium: false, elite: true },
    { category: 'Reporting', feature: 'Signal History & Performance Log', signals: true, pro: false, premium: true, elite: true },
    { category: 'Reporting', feature: 'Monthly AI Performance Report', signals: false, pro: false, premium: false, elite: true },
    { category: 'Access', feature: 'Early Access to New AI Features', signals: false, pro: false, premium: false, elite: true },
];

const FAQS = [
    { q: 'How does payment and activation work?', a: 'Activation is fully automated. Once your crypto payment via NOWPayments is confirmed on the blockchain, your account is instantly upgraded to your chosen tier.' },
    { q: 'What payment methods do you accept?', a: 'We exclusively accept cryptocurrencies (USDT, USDC, BTC, ETH, and over 50 others) via our automated NOWPayments gateway for seamless, global access.' },
    { q: 'Can I switch plans?', a: 'Yes. You can upgrade to a higher tier at any time by selecting the new plan on this page and completing the checkout. Your account will automatically reflect the new tier.' },
    { q: 'Will I lose access if my plan expires?', a: 'Yes — your premium access will be suspended after your 31-day cycle expires. You can renew seamlessly at any time through the pricing dashboard.' },
    { q: 'Are there refunds?', a: 'Due to the non-returnable nature of digital intelligence and live AI data, all payments are final. You can join our free Telegram channel to evaluate our edge before subscribing.' },
    { q: 'Which pairs do signals cover?', a: 'All current signal plans are exclusively focused on Gold (XAU/USD) to ensure the highest quality of AI edge and execution.' },
    { q: 'What is Autotrade?', a: 'Autotrade connects your ForexFund account directly to your MetaTrader 5 terminal. When our Neural AI generates a signal, it is automatically executed on your MT5 with your predefined risk settings — entirely hands-off.' },
];

// ─── Cell Renderer ─────────────────────────────────────────────────────────────
const Cell: React.FC<{ val: boolean | string }> = ({ val }) => {
    if (val === true) return <Check className="w-4 h-4 text-emerald-400 mx-auto" />;
    if (val === false) return <X className="w-4 h-4 text-gray-700 mx-auto" />;
    return <span className="text-[#D4A537] font-bold text-xs">{val}</span>;
};

// ─── Feature Icons ─────────────────────────────────────────────────────────────
const FEATURE_TILES = [
    { icon: Brain, label: 'Fundamental Analysis', color: '#D4A537', desc: 'GDP, CPI, NFP & central bank divergence synthesised into a directional bias score — updated live.' },
    { icon: Layers, label: 'Order Flow', color: '#10B981', desc: 'Algorithmic order block, FVG & liquidity zone mapping across 12 pairs and 5 timeframes.' },
    { icon: PieChart, label: 'COT Report', color: '#6366f1', desc: 'CFTC hedge fund positioning visualised weekly with extreme level alerts & divergence signals.' },
    { icon: BarChart2, label: 'Chart & Insight', color: '#D4A537', desc: 'Multi-timeframe AI analysis — market structure breaks, CHoCH, premium/discount zones.' },
    { icon: TrendingUp, label: 'Standard Signals', color: '#10B981', desc: '3-layer validated Gold signals with Structural SL/TP — delivered via dashboard + Telegram.' },
    { icon: Zap, label: 'Elite Signals', color: '#D4A537', desc: '90%+ conviction setups across deep-layer validated signals with priority delivery and dynamic autotrade sizing.' },
];

export const Pricing: React.FC = () => {
    const [loading, setLoading] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [openFaq, setOpenFaq] = useState<number | null>(null);
    const navigate = useNavigate();

    // Ensure page loads at the top and reset loading state
    useEffect(() => {
        window.scrollTo(0, 0);
        setLoading(null);
    }, []);

    // ── GA4 helper ─────────────────────────────────────────────────
    const trackGA4 = (eventName: string, params: Record<string, any>) => {
        if (typeof (window as any).gtag === 'function') {
            (window as any).gtag('event', eventName, params);
        }
    };

    const handleSubscribe = async (plan: typeof PLANS[0]) => {
        setLoading(plan.planKey);
        setError(null);

        // GA4: begin_checkout — fires when user clicks a plan CTA
        trackGA4('begin_checkout', {
            currency: 'USD',
            value: plan.amount,
            items: [{ item_id: plan.planKey, item_name: `ForexFundAI ${plan.name}`, price: plan.amount, quantity: 1 }],
        });

        try {
            let redirectUrl = '';
            if (plan.planKey === 'Signals') redirectUrl = 'https://nowpayments.io/payment/?iid=4328694785';
            else if (plan.planKey === 'Pro') redirectUrl = 'https://nowpayments.io/payment/?iid=5164894137';
            else if (plan.planKey === 'Premium') redirectUrl = 'https://nowpayments.io/payment/?iid=6137395487';
            else if (plan.planKey === 'Elite') redirectUrl = 'https://nowpayments.io/payment/?iid=6191855210';

            const { data: { session } } = await supabase.auth.getSession();

            if (!session) {
                // Not logged in -> GA4: sign_up intent, redirect to signup
                trackGA4('sign_up', { method: 'pricing_page', plan: plan.planKey });
                navigate(`/login?view=signup&redirect_to=${encodeURIComponent(redirectUrl)}`);
                return;
            }

            // GA4: generate_lead — logged-in user proceeding to payment
            trackGA4('generate_lead', {
                currency: 'USD',
                value: plan.amount,
                plan: plan.planKey,
            });

            // Logged in -> redirect directly to NOWPayments link
            // GA4: purchase fires on the NOWPayments success callback (handled server-side)
            // For client-side fallback, fire it here before redirect:
            trackGA4('purchase', {
                transaction_id: `${plan.planKey}_${Date.now()}`,
                currency: 'USD',
                value: plan.amount,
                items: [{ item_id: plan.planKey, item_name: `ForexFundAI ${plan.name}`, price: plan.amount, quantity: 1 }],
            });

            window.location.href = redirectUrl;

        } catch (err: any) {
            setError(err.message || 'Something went wrong.');
            setLoading(null);
        }
    };

    const categories = [...new Set(COMPARE_ROWS.map(r => r.category))];

    return (
        <div className="min-h-screen bg-[#060810] py-16">
            <Helmet>
                <title>ForexFund AI Pricing — Institutional Trading Edge from $30/Month</title>
                <meta name="description" content="Access professional-grade forex intelligence. Plans start at $30/month for deep macro research, AI signals, and autotrade. View institutional features and trial options." />
                <link rel="canonical" href="https://www.forexfundai.com/pricing" />
                <script type="application/ld+json">
                    {`
                    {
                        "@context": "https://schema.org",
                        "@type": "SoftwareApplication",
                        "name": "ForexFundAI",
                        "applicationCategory": "FinanceApplication",
                        "aggregateRating": {
                            "@type": "AggregateRating",
                            "ratingValue": "4.9",
                            "reviewCount": "2150"
                        },
                        "offers": {
                            "@type": "Offer",
                            "price": "30.00",
                            "priceCurrency": "USD"
                        }
                    }
                    `}
                </script>
            </Helmet>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

                {/* ── Page Header ─────────────────────────────────── */}
                <div className="text-center mb-16">
                    <h2 className="inline-block px-4 py-1.5 bg-[#D4A537]/10 border border-[#D4A537]/30 rounded-full text-[#D4A537] text-sm font-black tracking-[2px] uppercase mb-6">
                        Pricing Plans
                    </h2>
                    <h1 className="text-5xl md:text-7xl font-black text-white tracking-tight mb-4 leading-none">
                        Institutional Edge.<br />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#F0C060] via-[#D4A537] to-[#A07820]">No Compromises.</span>
                    </h1>
                    <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                        Four tiers. One goal — to give you the same intelligence institutional trading desks use every day.
                    </p>
                </div>


                {error && <div className="max-w-2xl mx-auto mb-8 bg-red-500/10 border border-red-500/30 text-red-400 p-4 rounded-xl text-center">{error}</div>}
                {success && <div className="max-w-2xl mx-auto mb-8 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 p-4 rounded-xl text-center">{success}</div>}

                {/* ── Plans Grid — 2 × 2 Glassmorphism ─────────────────── */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto mb-24">
                    {PLANS.map((plan) => (
                        <div
                            key={plan.name}
                            className="relative flex flex-col p-8 rounded-3xl transition-all duration-300 hover:-translate-y-2 hover:scale-[1.01] group"
                            style={{
                                background: plan.popular
                                    ? `linear-gradient(135deg, ${plan.color}18 0%, rgba(6,8,16,0.95) 60%)`
                                    : 'linear-gradient(135deg, rgba(255,255,255,0.04) 0%, rgba(6,8,16,0.97) 70%)',
                                border: plan.popular
                                    ? `1.5px solid ${plan.color}55`
                                    : '1.5px solid rgba(255,255,255,0.08)',
                                boxShadow: plan.popular
                                    ? `0 0 80px ${plan.color}20, 0 20px 60px rgba(0,0,0,0.4), inset 0 1px 0 ${plan.color}30`
                                    : '0 8px 40px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.06)',
                                backdropFilter: 'blur(20px)',
                            }}
                        >
                            {/* Glow orb for popular plan */}
                            {plan.popular && (
                                <div
                                    className="absolute -top-10 -right-10 w-48 h-48 rounded-full opacity-20 blur-3xl pointer-events-none"
                                    style={{ background: plan.color }}
                                />
                            )}

                            {/* Badge */}
                            {plan.badge && (
                                <div className="absolute -top-4 inset-x-0 flex justify-center">
                                    <span
                                        className="px-5 py-1.5 rounded-full text-[11px] font-black uppercase tracking-widest shadow-lg"
                                        style={{
                                            background: `linear-gradient(135deg, ${plan.color}, ${plan.color}cc)`,
                                            color: ['#D4A537', '#10B981', '#229ED9'].includes(plan.color) ? '#000' : '#fff',
                                            boxShadow: `0 4px 20px ${plan.color}40`,
                                        }}
                                    >
                                        {plan.badge}
                                    </span>
                                </div>
                            )}

                            {/* Plan header */}
                            <div className="mb-6 relative z-10">
                                <div className="text-[11px] font-black uppercase tracking-[3px] mb-2" style={{ color: plan.color }}>
                                    {plan.tagline}
                                </div>
                                <h3 className="text-3xl font-black text-white mb-3">{plan.name}</h3>
                                <div className="flex items-baseline gap-1 mb-4">
                                    <span className="text-6xl font-black text-white tracking-tight">{plan.price}</span>
                                    <span className="text-gray-500 font-bold">{plan.period}</span>
                                </div>
                                <p className="text-gray-400 text-sm leading-relaxed">{plan.description}</p>
                            </div>

                            {/* Divider */}
                            <div className="h-px mb-6 relative z-10" style={{ background: `linear-gradient(to right, ${plan.color}40, transparent)` }} />

                            {/* Features */}
                            <ul className="flex-1 space-y-3 mb-8 relative z-10">
                                {plan.features.map((f, i) => (
                                    <li key={i} className="flex items-start gap-3 text-sm text-gray-300">
                                        <div className="w-5 h-5 rounded-lg flex items-center justify-center shrink-0 mt-0.5" style={{ background: `${plan.color}18`, border: `1px solid ${plan.color}30` }}>
                                            <Check className="w-3 h-3" style={{ color: plan.color }} />
                                        </div>
                                        {f}
                                    </li>
                                ))}
                            </ul>

                            {/* CTA */}
                            <button
                                onClick={() => handleSubscribe(plan)}
                                disabled={loading === plan.planKey}
                                className="w-full py-4 rounded-2xl font-black text-sm uppercase tracking-widest transition-all duration-300 disabled:opacity-60 hover:brightness-110 hover:scale-[1.02] relative overflow-hidden group/btn z-10"
                                style={{
                                    background: `linear-gradient(135deg, ${plan.color} 0%, ${plan.color}bb 100%)`,
                                    color: ['#D4A537', '#10B981', '#229ED9'].includes(plan.color) ? '#000' : '#fff',
                                    boxShadow: `0 8px 32px ${plan.color}35`,
                                }}
                            >
                                <span className="relative z-10">
                                    {loading === plan.planKey ? 'Redirecting...' : `Get ${plan.name} — ${plan.price}/mo`}
                                </span>
                                <div className="absolute inset-0 bg-white/10 translate-y-full group-hover/btn:translate-y-0 transition-transform duration-300" />
                            </button>
                        </div>
                    ))}
                </div>

                {/* ── Signal Flow Diagram — How Our AI Works ────────── */}
                <div className="max-w-5xl mx-auto mb-20 rounded-3xl p-8" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', backdropFilter: 'blur(12px)' }}>
                    <div className="text-center mb-8">
                        <span className="text-xs font-black uppercase tracking-[3px] text-[#229ED9] mb-3 block">How Our Signals Work</span>
                        <h2 className="text-3xl font-black text-white mb-2">From Market to Your Entry — In Seconds</h2>
                        <p className="text-gray-500 text-sm max-w-xl mx-auto">Our AI doesn't just screen charts — it applies the same multi-layer validation process that institutional trading desks use, then delivers a ready-to-trade signal.</p>
                    </div>

                    {/* Flow steps */}
                    <div className="grid grid-cols-1 sm:grid-cols-5 gap-3 items-center">
                        {[
                            { step: '01', title: 'Live Market Data', desc: 'XAU/USD candles pulled every 15 minutes from institutional feeds', color: '#229ED9', icon: Activity },
                            { step: '02', title: 'Neural AI Analysis', desc: 'Multi-model AI scans 53+ strategies + ICT/SMC market structure', color: '#6366f1', icon: Brain },
                            { step: '03', title: '3-Layer Validation', desc: 'Macro bias + Order flow + COT positioning must align', color: '#10B981', icon: Layers },
                            { step: '04', title: 'Signal Generated', desc: 'Entry, Structural SL, TP1 & TP2 calculated with RR ratio', color: '#D4A537', icon: TrendingUp },
                            { step: '05', title: 'Auto-Delivered', desc: 'Dashboard + Telegram + MT5 autotrade — all simultaneously', color: '#229ED9', icon: Send },
                        ].map((item, i, arr) => (
                            <React.Fragment key={item.step}>
                                <div className="flex flex-col items-center text-center">
                                    <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-3" style={{ background: `${item.color}18`, border: `1px solid ${item.color}30` }}>
                                        <item.icon className="w-6 h-6" style={{ color: item.color }} />
                                    </div>
                                    <div className="text-[10px] font-black uppercase tracking-[2px] mb-1" style={{ color: item.color }}>Step {item.step}</div>
                                    <div className="text-white font-black text-xs mb-1">{item.title}</div>
                                    <div className="text-gray-500 text-[11px] leading-relaxed">{item.desc}</div>
                                </div>
                                {i < arr.length - 1 && (
                                    <div className="hidden sm:flex items-center justify-center text-gray-700 text-xl font-black">&rarr;</div>
                                )}
                            </React.Fragment>
                        ))}
                    </div>

                    {/* What makes us different */}
                    <div className="mt-10 pt-8 border-t border-white/5 grid grid-cols-1 sm:grid-cols-3 gap-4">
                        {[
                            { title: 'Backtested Strategies', desc: '53 elite strategies with Sharpe ≥ 2.0 & PF ≥ 1.3 — each proven on 20+ historical trades before going live', color: '#D4A537' },
                            { title: 'Transparent Track Record', desc: 'Every signal is recorded. Win rate, RR achieved, TP1/TP2 outcomes — all visible in Signal History', color: '#10B981' },
                            { title: 'Available on Telegram', desc: 'Every signal auto-posted to our Telegram channel instantly — join free, no subscription required', color: '#229ED9' },
                        ].map(d => (
                            <div key={d.title} className="bg-white/[0.02] rounded-2xl p-5 border border-white/5">
                                <div className="text-xs font-black uppercase tracking-widest mb-2" style={{ color: d.color }}>{d.title}</div>
                                <p className="text-gray-400 text-xs leading-relaxed">{d.desc}</p>
                            </div>
                        ))}
                    </div>

                    {/* Telegram CTA — public link */}
                    <a href="https://t.me/forexfundai" target="_blank" rel="noopener noreferrer"
                        className="mt-6 flex items-center justify-between gap-4 bg-[#229ED9]/10 border border-[#229ED9]/30 rounded-xl p-4 hover:bg-[#229ED9]/15 hover:border-[#229ED9]/50 transition-all group">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-[#229ED9]/20 flex items-center justify-center">
                                <Send className="w-5 h-5 text-[#229ED9]" />
                            </div>
                            <div>
                                <p className="text-white font-black text-sm">Try signals free on Telegram before subscribing</p>
                                <p className="text-gray-400 text-xs">All live signals are posted to our Telegram channel — join and see quality for yourself.</p>
                            </div>
                        </div>
                        <div className="px-4 py-2 rounded-xl bg-[#229ED9] text-white text-xs font-black uppercase tracking-widest whitespace-nowrap group-hover:brightness-110">
                            Join Free →
                        </div>
                    </a>
                </div>

                {/* ── Comparison Table ──────────────────────────────── */}
                <div className="mb-28 max-w-5xl mx-auto">
                    <div className="text-center mb-10">
                        <span className="text-xs font-black uppercase tracking-[3px] text-[#D4A537] mb-3 block">Compare Plans</span>
                        <h2 className="text-3xl md:text-4xl font-black text-white">Side-by-Side Breakdown</h2>
                        <p className="text-gray-500 mt-2">Every feature, across every plan — nothing hidden.</p>
                    </div>

                    <div className="overflow-x-auto pb-4 custom-scrollbar">
                        <div className="rounded-2xl overflow-hidden border border-white/8 shadow-2xl min-w-[800px]">
                            {/* Table Header */}
                            <div className="grid grid-cols-5 bg-white/[0.04] border-b border-white/8">
                                <div className="col-span-1 px-6 py-5 text-xs font-black uppercase tracking-widest text-gray-600">Feature</div>
                                {PLANS.map(p => (
                                    <div key={p.name} className="text-center px-4 py-5">
                                        <div className="text-xs font-black uppercase tracking-widest mb-0.5" style={{ color: p.color }}>{p.name}</div>
                                        <div className="text-white font-black text-lg">{p.price}<span className="text-gray-600 text-xs font-normal">/mo</span></div>
                                    </div>
                                ))}
                            </div>

                            {/* Table Rows Grouped by Category */}
                            {categories.map((cat) => (
                                <div key={cat}>
                                    {/* Category header */}
                                    <div className="grid grid-cols-5 bg-white/[0.02] border-y border-white/5">
                                        <div className="col-span-5 px-6 py-2">
                                            <span className="text-[11px] font-black uppercase tracking-[2px] text-gray-600">{cat}</span>
                                        </div>
                                    </div>
                                    {COMPARE_ROWS.filter(r => r.category === cat).map((row, ri) => (
                                        <div key={ri}
                                            className={`grid grid-cols-5 items-center border-b border-white/[0.03] hover:bg-white/[0.02] transition-colors ${ri % 2 === 0 ? '' : 'bg-white/[0.01]'}`}>
                                            <div className="col-span-1 px-6 py-3.5 text-gray-400 text-xs leading-relaxed pr-4">{row.feature}</div>
                                            <div className="text-center px-4 py-3.5"><Cell val={row.signals as boolean | string} /></div>
                                            <div className="text-center px-4 py-3.5"><Cell val={row.pro} /></div>
                                            <div className="text-center px-4 py-3.5"><Cell val={row.premium} /></div>
                                            <div className="text-center px-4 py-3.5"><Cell val={row.elite} /></div>
                                        </div>
                                    ))}
                                </div>
                            ))}

                            {/* Table Footer CTA Row */}
                            <div className="grid grid-cols-5 bg-white/[0.03] border-t border-white/8">
                                <div className="col-span-1 px-6 py-5 text-gray-600 text-xs italic">Instant Crypto Checkout</div>
                                {PLANS.map(p => (
                                    <div key={p.name} className="text-center px-4 py-5">
                                        <button
                                            onClick={() => handleSubscribe(p)}
                                            disabled={loading === p.planKey}
                                            className="px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all hover:brightness-110 disabled:opacity-50"
                                            style={{
                                                background: `linear-gradient(135deg, ${p.color}, ${p.color}aa)`,
                                                color: ['#D4A537', '#10B981', '#229ED9'].includes(p.color) ? '#000' : '#fff',
                                            }}>
                                            {loading === p.planKey ? 'Redirecting...' : `Pay via NOWPayments`}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── FAQ Section ───────────────────────────────────── */}
                <div className="max-w-3xl mx-auto">
                    <div className="text-center mb-10">
                        <h2 className="text-3xl font-black text-white mb-2">Frequently Asked Questions</h2>
                        <p className="text-gray-400">Everything you need to know before subscribing.</p>
                    </div>
                    <div className="space-y-3">
                        {FAQS.map((faq, i) => (
                            <div key={i} className="bg-white/[0.02] border border-white/5 rounded-xl overflow-hidden hover:border-white/10 transition-colors">
                                <button
                                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                                    className="w-full flex justify-between items-center px-6 py-4 text-left gap-4"
                                >
                                    <span className="text-white font-semibold text-sm">{faq.q}</span>
                                    {openFaq === i
                                        ? <ChevronUp className="w-5 h-5 text-[#D4A537] shrink-0" />
                                        : <ChevronDown className="w-5 h-5 text-gray-500 shrink-0" />}
                                </button>
                                {openFaq === i && (
                                    <div className="px-6 pb-5 text-gray-400 text-sm leading-relaxed border-t border-white/5 pt-3">
                                        {faq.a}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

            </div>
        </div>
    );
};
